<template>
  <div>
    <div class="text-center d-flex">
      <GradientButton
        v-if="testator.hasActivePayment && !whitelabel.will_async"
        @gradientButtonClicked="onDownloadClick()"
        :title="'Download Will'"
        :small="true"
        :fill="buttonFill"
        :callout="'right'"
        :disabled="disabled"
        :loading="buttonLoading"
      />
      <GradientButton
        v-if="testator.hasActivePayment && whitelabel.will_async"
        @gradientButtonClicked="onDownloadClick()"
        :title="downloadText"
        :small="true"
        :fill="buttonFill"
        :callout="'right'"
        :disabled="disabled"
        :loading="buttonLoading"
      />
    </div>

    <WillLanguageDialog
      :testator="testator"
      :willLanguageDialog="showWillLanguageDialog"
      :buttonLoading="buttonLoading"
      :allowedLanguageCodes="companyWillDownloadLanguages"
      :showEmailOption="isAdviserControllingTestator"
      @willLanguageDialog="handleLanguageDialogClose"
      @languageSelected="handleLanguageSelection"
    />

    <ErrorSnackbar v-model="showError" :error="errorMessage" />
  </div>
</template>

<script>
  import { GET_NEW_WILL_MUTATION } from '../../constants/graphql/wills';
  import { GENERATE_NEW_WILL_MUTATION } from '../../constants/graphql/wills';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import { TESTATOR_STEPS_QUERY } from '../../constants/graphql/overview';
  import { CREATE_UPLOADED_WILL_PRESIGNED_URL_MUTATION } from '../../constants/graphql/will-verifications';
  import { UPDATE_USER_LOCALE_MUTATION } from '../../constants/graphql/users';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import WillLanguageDialog from './dialogs/WillLanguageDialog.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import { EventBus } from '../../constants/js/event-bus';
  import { latestUploadedWill } from '../../constants/js/will-verification';

  export default {
    name: 'DownloadWill',
    components: {
      ErrorSnackbar,
      WillLanguageDialog,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        required: true,
        type: Object,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      callToAction: {
        type: Boolean,
        default: true,
      },
      willDetailsChanged: {
        required: true,
        default: false,
        type: Boolean,
      },
    },
    data() {
      return {
        errorMessage: null,
        intervalId: null,
        newTab: null,
        downloadText: 'Processing Will',
        showWillLanguageDialog: false,
        selectedLanguage: 'ENGLISH',
      };
    },
    computed: {
      buttonFill() {
        return this.willDetailsChanged;
      },
      showLanguageDialogCondition() {
        const langs = this.testator?.company?.willDownloadLanguages;
        return Array.isArray(langs) && langs.length > 1;
      },
      companyWillDownloadLanguages() {
        return this.testator?.company?.willDownloadLanguages || ['en'];
      },
      // An adviser using "log in as testator". Admins and call centre agents
      // controlling a testator do NOT get the email option (they always email).
      isAdviserControllingTestator() {
        return (
          !!this.$store.getters.isLoggedInAsSomeoneElse &&
          this.$store.getters.role === 'advisor'
        );
      },
    },
    created() {
      if (!this.whitelabel.will_async) {
        this.buttonLoading = true;
        this.getWill();
      } else {
        this.intervalId = setInterval(this.getWill, 3000);
        this.buttonLoading = true;
        this.getWill();
      }
      EventBus.$on('trigger-download-will', this.downloadWill);
      EventBus.$on(
        'trigger-download-will-with-language',
        this.handleLanguageSelection
      );
      EventBus.$on('view-latest-will', this.viewLatestWill);
      EventBus.$on('view-latest-uploaded-will', this.viewLatestUploadedWill);
    },
    beforeDestroy() {
      EventBus.$off('trigger-download-will', this.downloadWill);
      EventBus.$off(
        'trigger-download-will-with-language',
        this.handleLanguageSelection
      );
      EventBus.$off('view-latest-will', this.viewLatestWill);
      EventBus.$off('view-latest-uploaded-will', this.viewLatestUploadedWill);
      if (this.testator?.user?.locale) {
        this.selectedLanguage =
          this.testator.user.locale === 'en' ? 'ENGLISH' : 'AFRIKAANS';
      } else {
        this.selectedLanguage = 'ENGLISH';
      }
    },
    methods: {
      isAndroid() {
        return /Android/i.test(navigator.userAgent);
      },
      viewLatestUploadedWill() {
        const upload = latestUploadedWill(this.testator?.willVersions || []);
        if (!upload) return;
        this.$apollo
          .mutate({
            mutation: CREATE_UPLOADED_WILL_PRESIGNED_URL_MUTATION,
            variables: { id: upload.id },
          })
          .then(response => {
            const url = response.data.createUploadedWillPresignedUrl;
            if (url) {
              window.open(url, '_blank');
            }
          })
          .catch(error => {
            throw error;
          });
      },
      viewLatestWill() {
        this.$apollo
          .mutate({
            mutation: GET_NEW_WILL_MUTATION,
            variables: { testatorId: this.testator.id },
          })
          .then(response => {
            const url = response.data.getWill.url;
            if (url) {
              window.open(url, '_blank');
            }
          })
          .catch(error => {
            throw error;
          });
      },
      onDownloadClick() {
        if (this.showLanguageDialogCondition) {
          this.showWillLanguageDialog = true;
        } else {
          this.downloadWill();
        }
      },
      showLanguageDialog() {
        this.showWillLanguageDialog = true;
      },
      handleLanguageDialogClose(value) {
        this.showWillLanguageDialog = value;
      },
      // emailTestator is only ever false when the adviser unticks
      // "Email me a copy of the will" in the language dialog. It is passed
      // through as a parameter (never stored) so every other entry point
      // (assistance guide event, single-language companies) keeps emailing.
      handleLanguageSelection(language, emailTestator = true) {
        this.selectedLanguage = language;
        this.showWillLanguageDialog = false;
        this.updateUserLocale(language, emailTestator);
      },
      updateUserLocale(language, emailTestator = true) {
        const localeMap = {
          ENGLISH: 'en',
          AFRIKAANS: 'af',
          ZULU: 'zu',
          XHOSA: 'xh',
          SOTHO: 'nso',
        };

        const locale = localeMap[language];
        const id = this.userId;

        this.$apollo
          .mutate({
            mutation: UPDATE_USER_LOCALE_MUTATION,
            variables: {
              id,
              locale,
            },
            update: (cache, { data: { updateUserLocale } }) => {
              this.updateCacheAfterUpdateUser(cache, updateUserLocale);
            },
          })
          .then(_response => {
            this.downloadWill(emailTestator);
          })
          .catch(error => {
            console.warn('Failed to update user locale:', error);
          });
      },
      updateCacheAfterUpdateUser(cache, updateUser) {
        try {
          const query = {
            query: TESTATOR_QUERY,
            variables: { id: this.testatorId },
          };
          const data = cache.readQuery(query);

          if (data && data.testator && data.testator.user) {
            data.testator.user.locale = updateUser.locale;
            cache.writeQuery({ ...query, data });
          }
        } catch (error) {
          console.warn('Failed to update cache:', error);
        }
      },
      downloadWill(emailTestator = true) {
        if (this.isLoggedInAsSomeoneElse) {
          dataLayer.push({
            event: 'downloaded_wills',
            omuserid: this.$store.getters?.userId,
            omtid: this.testator.adTrackRecord?.omtId || 'Direct Traffic',
            emailaddress: this.testator.email,
            phonenumber: this.testator.cellNumber,
            willcompletedby: 'wills_completed_by_agent',
          });
        } else {
          dataLayer.push({
            event: 'downloaded_wills',
            omuserid: this.$store.getters?.userId,
            omtid: this.testator.adTrackRecord?.omtId || 'Direct Traffic',
            emailaddress: this.testator.email,
            phonenumber: this.testator.cellNumber,
            willcompletedby: 'wills_completed_online',
          });
        }
        this.generateWill(emailTestator);
      },
      generateWill(emailTestator = true) {
        this.buttonLoading = true;
        if (!this.whitelabel.will_async) {
          this.newTab = window.open('/generating-will', '_blank');
        }

        this.$apollo
          .mutate({
            mutation: GENERATE_NEW_WILL_MUTATION,
            variables: {
              testatorId: this.testator.id,
              emailTestator: emailTestator !== false,
            },
            context: {
              fetchOptions: {
                signal: AbortSignal.timeout(60000),
              },
            },
            update: (cache, { data: { downloadWill } }) => {
              if (downloadWill.url) {
                this.updateCacheForTestatorQuery(
                  cache,
                  downloadWill.willVersion
                );
                this.updateCacheForTestatorStepsQuery(
                  cache,
                  downloadWill.willVersion
                );
              }
            },
          })
          .then(response => {
            if (response.data.downloadWill.url) {
              if (this.isAndroid()) {
                this.newTab.close();
                window.open(response.data.downloadWill.url, '_blank');
              } else {
                this.newTab.location.href = response.data.downloadWill.url;
                this.newTab = null;
              }
              this.buttonLoading = false;
              this.$emit('willDownloaded');
            } else {
              if (!this.whitelabel.will_async) {
                // Do nothing
              } else {
                this.intervalId = setInterval(this.getWill, 3000);
              }
            }
          })
          .catch(error => {
            this.buttonLoading = false;
            if (error.message.includes('GraphQL error: Executor invalid')) {
              this.errorMessage =
                'Your Executor choice appears to be invalid, please edit your Executor in your people overview to resolve the issue.';
              this.newTab.location.href = 'generating-will?error=2';
            } else {
              this.newTab.location.href = 'generating-will?error=1';
              throw error;
            }
            this.displayError();
          });
      },
      getWill() {
        this.$apollo
          .mutate({
            mutation: GET_NEW_WILL_MUTATION,
            variables: {
              testatorId: this.testatorId,
            },
            update: (cache, { data: { getWill } }) => {
              if (
                getWill.url &&
                getWill.willVersion &&
                getWill.willVersion.processed === true
              ) {
                this.updateCacheForTestatorQuery(cache, getWill.willVersion);
                this.updateCacheForTestatorStepsQuery(
                  cache,
                  getWill.willVersion
                );
              }
            },
          })
          .then(response => {
            if (response.data.getWill.processWill) {
              this.downloadText = 'Process Will';
              clearInterval(this.intervalId);
              this.buttonLoading = false;
            } else if (
              response.data.getWill.url &&
              response.data.getWill.willVersion &&
              response.data.getWill.willVersion.processed === true
            ) {
              this.downloadText = 'Download Will';
              clearInterval(this.intervalId);
              this.buttonLoading = false;
            }
          })
          .catch(error => {
            this.buttonLoading = false;
            throw error;
          });
      },

      updateCacheForTestatorQuery(cache, willVersion) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        let data;
        try {
          data = cache.readQuery(query);
        } catch (e) {
          // Query isn't in cache (or is missing fields); nothing to update.
          return;
        }
        if (!data || !data.testator) return;
        // Only add willVersion to testator if its a new willVersion
        if (
          !data.testator.willVersions.find(
            existingWillVersion => existingWillVersion.id == willVersion.id
          )
        ) {
          data.testator.willVersions.push(willVersion);
          cache.writeQuery({ ...query, data: data });
        }
      },
      updateCacheForTestatorStepsQuery(cache, willVersion) {
        const query = {
          query: TESTATOR_STEPS_QUERY,
          variables: { id: this.testatorId },
        };
        let data;
        try {
          data = cache.readQuery(query);
        } catch (e) {
          // Query isn't in cache (or is missing fields); nothing to update.
          return;
        }
        if (!data || !data.testator) return;

        if (!Array.isArray(data.testator.willVersions)) {
          data.testator.willVersions = [];
        }
        if (typeof data.testator.willDetailsChanged === 'undefined') {
          data.testator.willDetailsChanged = false;
        }
        if (typeof data.testator.previewWillDetailsChanged === 'undefined') {
          data.testator.previewWillDetailsChanged = false;
        }

        // Only add willVersion to testator if its a new willVersion
        if (
          !data.testator.willVersions.find(
            existingWillVersion => existingWillVersion.id == willVersion.id
          )
        ) {
          data.testator.willVersions.push(willVersion);
        }
        // Latest version created therefore all details up to date
        data.testator.willDetailsChanged = false;
        cache.writeQuery({ ...query, data: data });
      },
    },
  };
</script>
<style>
  .custom-loader {
    animation: loader 1s infinite;
    display: flex;
  }
  @-moz-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @-webkit-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @-o-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
</style>
